// src/pages/AdminFilteredSystem.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Building2,
  Filter,
  Search,
  Download,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

// ────────────────────────────────────────────────
// Colors & Status Styles
// ────────────────────────────────────────────────
const COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#14b8a6', '#f59e0b', '#ef4444', '#10b981',
];

const statusStyles = {
  Applied: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  Shortlisted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  Interviewed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  Offer: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  Rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  Accepted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  Scheduled: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  Online: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  Draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  'Awaiting Approval': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  Offline: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

// ────────────────────────────────────────────────
// Filter & Column Configurations (NOW DEFINED)
// ────────────────────────────────────────────────

const filterFieldsConfig = {
  applications: [
    { name: 'jobTitle', label: 'Job Title', type: 'text' },
    { name: 'jobType', label: 'Job Type', type: 'text' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: ['Applied', 'Shortlisted', 'Interviewed', 'Offer', 'Rejected', 'Accepted', 'Scheduled'],
    },
    { name: 'position', label: 'Position', type: 'text' },
    { name: 'state', label: 'State', type: 'text' },
    { name: 'localGovernment', label: 'LGA', type: 'text' },
  ],
  jobs: [
    { name: 'jobTitle', label: 'Job Title', type: 'text' },
    { name: 'jobType', label: 'Job Type', type: 'text' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: ['Draft', 'Awaiting Approval', 'Online', 'Offline', 'Rejected'],
    },
    { name: 'industry', label: 'Industry', type: 'text' },
    { name: 'state', label: 'State', type: 'text' },
  ],
  seekers: [
    { name: 'state', label: 'State', type: 'text' },
    { name: 'localGovernment', label: 'LGA', type: 'text' },
    { name: 'gender', label: 'Gender', type: 'text' },
    { name: 'educationLevel', label: 'Education Level', type: 'text' },
    { name: 'jobMode', label: 'Job Mode', type: 'text' },
  ],
  employers: [
    { name: 'companyName', label: 'Company Name', type: 'text' },
    { name: 'state', label: 'State', type: 'text' },
    { name: 'localGovernment', label: 'LGA', type: 'text' },
    { name: 'employerType', label: 'Employer Type', type: 'text' },
  ],
};

const columnsConfig = {
  applications: [
    { key: 'job.jobTitle', label: 'Job Title' },
    { key: 'job.jobType', label: 'Type' },
    { key: 'position', label: 'Position' },
    {
      key: 'status',
      label: 'Status',
      render: (v) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusStyles[v] || 'bg-gray-100 dark:bg-gray-800'}`}>
          {v}
        </span>
      ),
    },
    { key: 'applicant.firstName', label: 'First Name' },
    { key: 'applicant.lastName', label: 'Last Name' },
    { key: 'companyId.companyName', label: 'Company' },
    { key: 'createdAt', label: 'Date', render: (v) => new Date(v).toLocaleDateString() },
  ],
  jobs: [
    { key: 'jobTitle', label: 'Title' },
    { key: 'jobType', label: 'Type' },
    {
      key: 'status',
      label: 'Status',
      render: (v) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusStyles[v] || 'bg-gray-100 dark:bg-gray-800'}`}>
          {v}
        </span>
      ),
    },
    { key: 'industry', label: 'Industry' },
    { key: 'salary', label: 'Salary' },
    { key: 'state', label: 'State' },
    { key: 'employer.companyName', label: 'Employer' },
  ],
  seekers: [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'state', label: 'State' },
    { key: 'gender', label: 'Gender' },
    { key: 'educationLevel', label: 'Education' },
  ],
  employers: [
    { key: 'companyName', label: 'Company' },
    { key: 'email', label: 'Email' },
    { key: 'state', label: 'State' },
    { key: 'employerType', label: 'Type' },
    { key: 'size', label: 'Size' },
    { key: 'isVerified', label: 'Verified', render: (v) => (v ? 'Yes' : 'No') },
    { key: 'listedJobs.length', label: 'Jobs Posted' },
  ],
};

// ────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────
function getNestedValue(obj, path) {
  return path.split('.').reduce((o, k) => {
    if (k.endsWith('.length') && Array.isArray(o?.[k.replace('.length', '')])) {
      return o[k.replace('.length', '')].length;
    }
    return o?.[k];
  }, obj) ?? '—';
}

function computeChartData(dataArray, key) {
  if (!Array.isArray(dataArray)) return [];
  const counts = dataArray.reduce((acc, item) => {
    const value = getNestedValue(item, key) || 'Unknown';
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}

// ────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────
export default function AdminFilteredSystem() {
  const [activeTab, setActiveTab] = useState('stats');
  const [filtersOpen, setFiltersOpen] = useState(false); // Collapsible on mobile

  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [seekers, setSeekers] = useState([]);
  const [employers, setEmployers] = useState([]);

  const [filters, setFilters] = useState({
    applications: {},
    jobs: {},
    seekers: {},
    employers: {},
  });

  const [loading, setLoading] = useState({
    stats: false,
    applications: false,
    jobs: false,
    seekers: false,
    employers: false,
  });

  const token = localStorage.getItem('token');

  const fetchData = async (endpoint, queryFilters = {}, setter, loadingKey) => {
    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const params = new URLSearchParams(queryFilters).toString();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:2020/api'}admin/${endpoint}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setter(res.data || []);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
      toast.error(`Failed to load ${endpoint}`);
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  useEffect(() => {
    fetchData('stats', {}, setStats, 'stats');
  }, []);

  const applyFilter = (type) => {
    const endpointMap = {
      applications: 'applications',
      jobs: 'jobs',
      seekers: 'jobseekers',
      employers: 'employers',
    };
    const setterMap = {
      applications: setApplications,
      jobs: setJobs,
      seekers: setSeekers,
      employers: setEmployers,
    };

    fetchData(
      endpointMap[type],
      filters[type],
      setterMap[type],
      type
    );
  };

  const handleFilterChange = (type, e) => {
    setFilters((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [e.target.name]: e.target.value,
      },
    }));
  };

  const tabData = { applications, jobs, seekers, employers };
  const tabLoading = {
    applications: loading.applications,
    jobs: loading.jobs,
    seekers: loading.seekers,
    employers: loading.employers,
  };

  const currentTab = activeTab === 'stats' ? null : activeTab;
  const currentData = currentTab ? tabData[currentTab] : null;
  const currentLoading = currentTab ? tabLoading[currentTab] : false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 text-gray-900 dark:text-gray-100 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              Admin Control Center
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
              Monitor platform activity • Apply filters • Export insights
            </p>
          </div>
          <button className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all shadow-lg hover:shadow-xl">
            <Download size={18} />
            Export Full Report
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
            <nav className="flex px-4 md:px-6 -mb-px">
              {[
                { id: 'stats', label: 'Overview', icon: LayoutDashboard },
                { id: 'applications', label: 'Applications', icon: Briefcase },
                { id: 'jobs', label: 'Jobs', icon: Search },
                { id: 'seekers', label: 'Job Seekers', icon: Users },
                { id: 'employers', label: 'Employers', icon: Building2 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-5 py-4 text-sm md:text-base font-medium whitespace-nowrap transition-all flex-shrink-0 border-b-2
                    ${
                      activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-500 dark:text-indigo-400 bg-gradient-to-t from-indigo-50/50 to-transparent dark:from-indigo-950/30'
                        : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }
                  `}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 lg:p-10">
            {activeTab === 'stats' ? (
              <div className="space-y-12">
                {loading.stats ? (
                  <div className="flex justify-center items-center h-64">
                    <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
                  </div>
                ) : stats ? (
                  <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {[
                        { title: 'Total Applications', value: stats.totalApplications?.toLocaleString() || '0', color: 'indigo' },
                        { title: 'Total Jobs', value: stats.totalJobs?.toLocaleString() || '0', color: 'green' },
                        { title: 'Job Seekers', value: stats.totalJobSeekers?.toLocaleString() || '0', color: 'purple' },
                        { title: 'Employers', value: stats.totalEmployers?.toLocaleString() || '0', color: 'amber' },
                      ].map((card, i) => (
                        <div
                          key={i}
                          className={`p-6 rounded-2xl border shadow-sm transition-all hover:shadow-lg hover:scale-[1.02] bg-gradient-to-br from-${card.color}-50 to-white dark:from-${card.color}-950/30 dark:to-gray-900`}
                        >
                          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                            {card.title}
                          </h3>
                          <p className={`text-3xl md:text-4xl font-extrabold text-${card.color}-700 dark:text-${card.color}-300`}>
                            {card.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
                        <h3 className="text-xl font-bold mb-6 text-center text-gray-900 dark:text-white">
                          Applications by Status
                        </h3>
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={computeChartData(stats.applicationsByStatus || [], '_id')}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={110}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {computeChartData(stats.applicationsByStatus || [], '_id').map((_, i) => (
                                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
                        <h3 className="text-xl font-bold mb-6 text-center text-gray-900 dark:text-white">
                          Jobs by Type
                        </h3>
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={computeChartData(stats.jobsByType || [], '_id')}>
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#6366f1" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-20 bg-gray-50 dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800">
                    <p className="text-xl text-gray-500 dark:text-gray-400">
                      No statistics available yet
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-10">
                {/* Filter Panel */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center justify-between px-6 py-5 cursor-pointer bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-900"
                    onClick={() => setFiltersOpen(!filtersOpen)}
                  >
                    <div className="flex items-center gap-3">
                      <Filter size={20} className="text-indigo-600" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        Advanced Filters
                      </h3>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`text-gray-600 dark:text-gray-400 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {filtersOpen && (
                    <div className="p-6 lg:p-8 border-t border-gray-200 dark:border-gray-800">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {filterFieldsConfig[currentTab]?.map((field) => (
                          <div key={field.name}>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {field.label}
                            </label>
                            {field.type === 'select' ? (
                              <select
                                name={field.name}
                                value={filters[currentTab][field.name] || ''}
                                onChange={(e) => handleFilterChange(currentTab, e)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition shadow-sm"
                              >
                                <option value="">All {field.label}</option>
                                {field.options?.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.type || 'text'}
                                name={field.name}
                                value={filters[currentTab][field.name] || ''}
                                onChange={(e) => handleFilterChange(currentTab, e)}
                                placeholder={`Filter by ${field.label.toLowerCase()}`}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition shadow-sm"
                              />
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="mt-8 flex flex-col sm:flex-row gap-4">
                        <button
                          onClick={() => applyFilter(currentTab)}
                          className="flex-1 px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
                        >
                          Apply Filters
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, [currentTab]: {} });
                            applyFilter(currentTab);
                          }}
                          className="flex-1 px-8 py-3.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-xl font-medium transition-all"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Data Display */}
                {currentLoading ? (
                  <div className="flex justify-center items-center h-96">
                    <Loader2 className="h-14 w-14 animate-spin text-indigo-600" />
                  </div>
                ) : !currentData || currentData.length === 0 ? (
                  <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <p className="text-xl text-gray-500 dark:text-gray-400">
                      No {currentTab} found matching your filters
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Chart */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 lg:p-8 shadow-sm">
                      <h3 className="text-2xl font-bold mb-8 text-center text-gray-900 dark:text-white">
                        {currentTab === 'applications' ? 'Applications by Status' :
                         currentTab === 'jobs' ? 'Jobs by Type' :
                         currentTab === 'seekers' ? 'Seekers by Gender' :
                         'Employers by Type'}
                      </h3>
                      <div className="h-96 lg:h-[420px]">
                        <ResponsiveContainer width="100%" height="100%">
                          {currentTab === 'jobs' || currentTab === 'employers' ? (
                            <BarChart data={computeChartData(currentData, currentTab === 'jobs' ? 'jobType' : 'employerType')}>
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#6366f1" radius={[12, 12, 0, 0]} />
                            </BarChart>
                          ) : (
                            <PieChart>
                              <Pie
                                data={computeChartData(currentData, currentTab === 'applications' ? 'status' : 'gender')}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={140}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {computeChartData(currentData, currentTab === 'applications' ? 'status' : 'gender').map((_, i) => (
                                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend verticalAlign="bottom" height={40} />
                            </PieChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              {columnsConfig[currentTab]?.map((col) => (
                                <th
                                  key={col.key}
                                  className="px-6 py-5 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                            {currentData.map((item, idx) => (
                              <tr
                                key={idx}
                                className="hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30 transition-colors"
                              >
                                {columnsConfig[currentTab]?.map((col) => (
                                  <td
                                    key={col.key}
                                    className="px-6 py-5 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200"
                                  >
                                    {col.render
                                      ? col.render(getNestedValue(item, col.key))
                                      : getNestedValue(item, col.key) || '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}